import { assert } from 'tsafe'
import { identity } from './functional/function.no-deps'

export function flatten<T>(array: T[][]): T[] {
    return array.flatMap(identity)
}

export function compact<T>(array: T[]): T[] {
    return array.filter((entry) => entry != null)
}

export function replaceAt<T>(array: T[], index: number, newValue: T) {
    array = [...array]
    array[index] = newValue
    return array
}

export function removeAt<T>(array: T[], index: number): T[] {
    array = [...array]
    array.splice(index, 1)
    return array
}

export function insertAt<T>(array: T[], index: number, value: T): T[] {
    return [
        ...array.slice(0, index),
        value, //
        ...array.slice(index, array.length),
    ]
}

export function buildArray<T>(length: number, fn: (index: number) => T): T[] {
    return Array(length)
        .fill(null)
        .map((_, index) => fn(index))
}

export function findOrThrow<T>(array: T[], fn: (value: T) => boolean, errorMessage?: string) {
    const value = array.find(fn)

    if (value == null) {
        throw new Error(errorMessage || `Could not find value in ${array}`)
    }

    return value
}

export function replaceOrConcatBy<T>(array: T[], value: T, fn: (value: T) => boolean): T[] {
    const index = array.findIndex(fn)
    if (index >= 0) return replaceAt(array, index, value)
    return array.concat(value)
}

/**
 * Check if there is a value that is present in both arrays, left and right
 */
export function haveSharedMembers<T>(left: T[], right: T[]): boolean {
    return left.some((member) => right.includes(member))
}

/**
 * Check if all values in the right array are included in left array
 */
export function includesAllMembers<T>(left: T[], right: T[]): boolean {
    return right.every((member) => left.includes(member))
}

/**
 * Check if two arrays have the same values inside INDEPENDENT of order
 */
export function containsTheSameValues<T>(left: T[], right: T[]): boolean {
    return includesAllMembers(left, right) && includesAllMembers(right, left)
}

export function moveOneEarlier<T>(array: T[], index: number): T[] {
    assert(index > 0)
    return [
        ...array.slice(0, index - 1),
        array[index], //
        array[index - 1],
        ...array.slice(index + 1, array.length),
    ]
}

export function moveOneLater<T>(array: T[], index: number): T[] {
    assert(index < array.length - 1)
    return [
        ...array.slice(0, index),
        array[index + 1],
        array[index], //
        ...array.slice(index + 2, array.length),
    ]
}
